import { apiUrl } from './api-url';

export type HttpMethod = 'GET' | 'POST' | 'PATCH' | 'DELETE' | 'PUT';

export type ApiCatalogEntry = {
  group: string;
  method: HttpMethod;
  path: string;
};

/**
 * Inventory of every backend route the frontend calls.
 * Add a row here (and a matching `API` key) before using a new endpoint.
 */
export const API_CATALOG: readonly ApiCatalogEntry[] = [
  { group: 'Auth', method: 'POST', path: 'auth/login' },
  { group: 'Auth', method: 'POST', path: 'auth/refresh' },
  { group: 'Auth', method: 'POST', path: 'auth/logout' },
  { group: 'Auth', method: 'POST', path: 'auth/forgot-password' },
  { group: 'Auth', method: 'POST', path: 'auth/reset-password' },
  { group: 'Auth', method: 'POST', path: 'auth/change-password' },
  { group: 'Auth', method: 'GET', path: 'auth/me' },
  { group: 'Auth', method: 'PATCH', path: 'auth/me' },
  { group: 'Appearance', method: 'GET', path: 'appearance' },
  { group: 'Appearance', method: 'PATCH', path: 'appearance' },
  { group: 'Appearance', method: 'POST', path: 'appearance/reset' },
  { group: 'Dashboard', method: 'GET', path: 'dashboard' },
  { group: 'Designations', method: 'GET', path: 'designations' },
  { group: 'Designations', method: 'POST', path: 'designations' },
  { group: 'Designations', method: 'GET', path: 'designations/:id' },
  { group: 'Designations', method: 'PATCH', path: 'designations/:id' },
  { group: 'Designations', method: 'PATCH', path: 'designations/:id/access' },
  { group: 'Designations', method: 'DELETE', path: 'designations/:id' },
  { group: 'Departments', method: 'GET', path: 'departments' },
  { group: 'Departments', method: 'POST', path: 'departments' },
  { group: 'Departments', method: 'GET', path: 'departments/:id' },
  { group: 'Departments', method: 'PATCH', path: 'departments/:id' },
  { group: 'Departments', method: 'PATCH', path: 'departments/:id/access' },
  { group: 'Departments', method: 'DELETE', path: 'departments/:id' },
  { group: 'Plants', method: 'GET', path: 'plants' },
  { group: 'Plants', method: 'POST', path: 'plants' },
  { group: 'Plants', method: 'GET', path: 'plants/:id' },
  { group: 'Plants', method: 'PATCH', path: 'plants/:id' },
  { group: 'Plants', method: 'PATCH', path: 'plants/:id/access' },
  { group: 'Plants', method: 'DELETE', path: 'plants/:id' },
  { group: 'Workspaces', method: 'GET', path: 'workspaces' },
  { group: 'Workspaces', method: 'GET', path: 'workspaces/assigned' },
  { group: 'Workspaces', method: 'POST', path: 'workspaces' },
  { group: 'Workspaces', method: 'GET', path: 'workspaces/:id' },
  { group: 'Workspaces', method: 'PATCH', path: 'workspaces/:id' },
  { group: 'Workspaces', method: 'PATCH', path: 'workspaces/:id/access' },
  { group: 'Workspaces', method: 'DELETE', path: 'workspaces/:id' },
  { group: 'Workspaces', method: 'GET', path: 'workspaces/:id/candidates' },
  { group: 'Workspaces', method: 'GET', path: 'workspaces/:id/members' },
  { group: 'Workspaces', method: 'POST', path: 'workspaces/:id/members' },
  { group: 'Workspaces', method: 'PATCH', path: 'workspaces/:id/members/:memberId' },
  { group: 'Workspaces', method: 'DELETE', path: 'workspaces/:id/members/:memberId' },
  { group: 'Workspaces', method: 'GET', path: 'workspaces/:id/documents' },
  { group: 'Workspaces', method: 'GET', path: 'workspaces/:id/documents/:documentId/download' },
  { group: 'Workspaces', method: 'POST', path: 'workspaces/:id/folders' },
  { group: 'Workspaces', method: 'PATCH', path: 'workspaces/:id/folders/:folderId' },
  { group: 'Workspaces', method: 'DELETE', path: 'workspaces/:id/folders/:folderId' },
  { group: 'Workspaces', method: 'POST', path: 'workspaces/:id/folders/:folderId/documents' },
  { group: 'Workspaces', method: 'PATCH', path: 'workspaces/:id/documents/:documentId' },
  { group: 'Workspaces', method: 'DELETE', path: 'workspaces/:id/documents/:documentId' },
  { group: 'Projects', method: 'GET', path: 'projects' },
  { group: 'Projects', method: 'POST', path: 'projects' },
  { group: 'Projects', method: 'GET', path: 'projects/:id' },
  { group: 'Projects', method: 'PATCH', path: 'projects/:id' },
  { group: 'Projects', method: 'DELETE', path: 'projects/:id' },
  { group: 'Permissions', method: 'GET', path: 'private' },
  { group: 'Profile', method: 'GET', path: 'profile' },
  { group: 'Roles', method: 'GET', path: 'roles' },
  { group: 'Roles', method: 'POST', path: 'roles' },
  { group: 'Roles', method: 'GET', path: 'roles/:id' },
  { group: 'Roles', method: 'PATCH', path: 'roles/:id' },
  { group: 'Roles', method: 'DELETE', path: 'roles/:id' },
  { group: 'Users', method: 'GET', path: 'users' },
  { group: 'Users', method: 'POST', path: 'users' },
  { group: 'Users', method: 'GET', path: 'users/:id' },
  { group: 'Users', method: 'PATCH', path: 'users/:id' },
  { group: 'Users', method: 'PATCH', path: 'users/:id/access' },
  { group: 'Users', method: 'POST', path: 'users/:id/resend-invite' },
  { group: 'Users', method: 'DELETE', path: 'users/:id' },
  { group: 'Audit Trails', method: 'GET', path: 'audit-trails' },
  { group: 'Audit Trails', method: 'GET', path: 'audit-trails/filters' },
  { group: 'Audit Trails', method: 'GET', path: 'audit-trails/export' },
  { group: 'Audit Trails', method: 'GET', path: 'audit-trails/:id' },
  { group: 'Notifications', method: 'GET', path: 'organization-notifications' },
  { group: 'Notifications', method: 'PATCH', path: 'organization-notifications' },
  { group: 'Notifications', method: 'GET', path: 'notification-preferences' },
  { group: 'Notifications', method: 'PATCH', path: 'notification-preferences' },
  { group: 'Notifications', method: 'GET', path: 'in-app-notifications' },
  { group: 'Notifications', method: 'GET', path: 'in-app-notifications/unread-count' },
  { group: 'Notifications', method: 'POST', path: 'in-app-notifications/read-all' },
  { group: 'Notifications', method: 'PATCH', path: 'in-app-notifications/:id/read' },
  { group: 'Recruiting', method: 'GET', path: 'recruiting/dashboard' },
  { group: 'Recruiting', method: 'GET', path: 'recruiting/job-descriptions' },
  { group: 'Recruiting', method: 'POST', path: 'recruiting/job-descriptions' },
  { group: 'Recruiting', method: 'GET', path: 'recruiting/job-descriptions/:id' },
  { group: 'Recruiting', method: 'PUT', path: 'recruiting/job-descriptions/:id' },
  { group: 'Recruiting', method: 'PATCH', path: 'recruiting/job-descriptions/:id/archive' },
  { group: 'Recruiting', method: 'GET', path: 'recruiting/active-openings' },
  { group: 'Recruiting', method: 'GET', path: 'recruiting/active-openings/published-jds' },
  { group: 'Recruiting', method: 'POST', path: 'recruiting/active-openings' },
  { group: 'Recruiting', method: 'GET', path: 'recruiting/active-openings/:id' },
  { group: 'Recruiting', method: 'PUT', path: 'recruiting/active-openings/:id' },
  { group: 'Recruiting', method: 'PATCH', path: 'recruiting/active-openings/:id/close' },
  { group: 'Recruiting', method: 'GET', path: 'recruiting/candidates' },
  { group: 'Recruiting', method: 'POST', path: 'recruiting/candidates' },
  { group: 'Recruiting', method: 'GET', path: 'recruiting/candidates/:id' },
  { group: 'Recruiting', method: 'GET', path: 'recruiting/candidates/:id/cv' },
  { group: 'Recruiting', method: 'POST', path: 'recruiting/candidates/inbox/sync' },
  { group: 'Recruiting', method: 'GET', path: 'recruiting/candidates/inbox/sync/latest' },
  { group: 'Recruiting', method: 'POST', path: 'recruiting/candidates/:id/rescreen' },
  { group: 'Recruiting', method: 'POST', path: 'recruiting/candidates/rescreen-stuck' },
  { group: 'Recruiting', method: 'PATCH', path: 'recruiting/candidates/:id/assign-opening' },
  { group: 'Recruiting', method: 'PATCH', path: 'recruiting/candidates/:id/decision' },
  { group: 'Recruiting', method: 'PATCH', path: 'recruiting/candidates/:id/archive' },
  { group: 'Recruiting', method: 'GET', path: 'recruiting/mailbox' },
  { group: 'Recruiting', method: 'POST', path: 'recruiting/mailbox/connect' },
  { group: 'Recruiting', method: 'DELETE', path: 'recruiting/mailbox' },
  { group: 'Tasks', method: 'GET', path: 'tasks/board' },
  { group: 'Tasks', method: 'GET', path: 'tasks/stats' },
  { group: 'Tasks', method: 'GET', path: 'tasks/assignees' },
  { group: 'Tasks', method: 'GET', path: 'tasks' },
  { group: 'Tasks', method: 'POST', path: 'tasks' },
  { group: 'Tasks', method: 'GET', path: 'tasks/:id' },
  { group: 'Tasks', method: 'PATCH', path: 'tasks/:id' },
  { group: 'Tasks', method: 'DELETE', path: 'tasks/:id' },
  { group: 'Meetings', method: 'GET', path: 'meetings' },
  { group: 'Meetings', method: 'POST', path: 'meetings' },
  { group: 'Meetings', method: 'GET', path: 'meetings/:id' },
  { group: 'Meetings', method: 'PATCH', path: 'meetings/:id' },
  { group: 'Meetings', method: 'DELETE', path: 'meetings/:id' },
  { group: 'Meetings', method: 'POST', path: 'meetings/:id/recording' },
  { group: 'Meetings', method: 'POST', path: 'meetings/:id/transcript' },
  { group: 'Meetings', method: 'POST', path: 'meetings/:id/share' },
  { group: 'Meetings', method: 'GET', path: 'me/calendar-connections' },
  { group: 'Meetings', method: 'POST', path: 'me/calendar-connections/:provider/connect' },
  { group: 'Meetings', method: 'DELETE', path: 'me/calendar-connections/:provider' },
];

export const API = {
  auth: {
    login: 'auth/login',
    refresh: 'auth/refresh',
    logout: 'auth/logout',
    forgotPassword: 'auth/forgot-password',
    resetPassword: 'auth/reset-password',
    changePassword: 'auth/change-password',
    me: 'auth/me',
  },
  appearance: {
    root: 'appearance',
    reset: 'appearance/reset',
  },
  dashboard: 'dashboard',
  designations: {
    collection: 'designations',
    item: (id: string) => `designations/${id}`,
    access: (id: string) => `designations/${id}/access`,
  },
  departments: {
    collection: 'departments',
    item: (id: string) => `departments/${id}`,
    access: (id: string) => `departments/${id}/access`,
  },
  plants: {
    collection: 'plants',
    item: (id: string) => `plants/${id}`,
    access: (id: string) => `plants/${id}/access`,
  },
  workspaces: {
    collection: 'workspaces',
    assigned: 'workspaces/assigned',
    item: (id: string) => `workspaces/${id}`,
    access: (id: string) => `workspaces/${id}/access`,
    candidates: (id: string) => `workspaces/${id}/candidates`,
    members: (id: string) => `workspaces/${id}/members`,
    member: (id: string, memberId: string) => `workspaces/${id}/members/${memberId}`,
    documents: (id: string) => `workspaces/${id}/documents`,
    folders: (id: string) => `workspaces/${id}/folders`,
    folder: (id: string, folderId: string) => `workspaces/${id}/folders/${folderId}`,
    folderDocuments: (id: string, folderId: string) => `workspaces/${id}/folders/${folderId}/documents`,
    document: (id: string, documentId: string) => `workspaces/${id}/documents/${documentId}`,
    documentDownload: (id: string, documentId: string) => `workspaces/${id}/documents/${documentId}/download`,
  },
  projects: {
    collection: 'projects',
    item: (id: string) => `projects/${id}`,
  },
  permissions: {
    catalog: 'private',
  },
  profile: {
    root: 'profile',
  },
  roles: {
    collection: 'roles',
    item: (id: string) => `roles/${id}`,
  },
  users: {
    collection: 'users',
    item: (id: string) => `users/${id}`,
    access: (id: string) => `users/${id}/access`,
    resendInvite: (id: string) => `users/${id}/resend-invite`,
  },
  auditTrails: {
    collection: 'audit-trails',
    filters: 'audit-trails/filters',
    export: 'audit-trails/export',
    item: (id: string) => `audit-trails/${id}`,
  },
  notifications: {
    organization: 'organization-notifications',
    mine: 'notification-preferences',
    inbox: 'in-app-notifications',
    unreadCount: 'in-app-notifications/unread-count',
    readAll: 'in-app-notifications/read-all',
    read: (id: string) => `in-app-notifications/${id}/read`,
  },
  tasks: {
    collection: 'tasks',
    board: 'tasks/board',
    stats: 'tasks/stats',
    assignees: 'tasks/assignees',
    item: (id: string) => `tasks/${id}`,
  },
  recruiting: {
    dashboard: 'recruiting/dashboard',
    jobDescriptions: 'recruiting/job-descriptions',
    jobDescription: (id: string) => `recruiting/job-descriptions/${id}`,
    archiveJobDescription: (id: string) => `recruiting/job-descriptions/${id}/archive`,
    activeOpenings: 'recruiting/active-openings',
    activeOpening: (id: string) => `recruiting/active-openings/${id}`,
    publishedJds: 'recruiting/active-openings/published-jds',
    closeActiveOpening: (id: string) => `recruiting/active-openings/${id}/close`,
    candidates: 'recruiting/candidates',
    candidate: (id: string) => `recruiting/candidates/${id}`,
    candidateCv: (id: string) => `recruiting/candidates/${id}/cv`,
    candidateRescreen: (id: string) => `recruiting/candidates/${id}/rescreen`,
    candidatesRescreenStuck: 'recruiting/candidates/rescreen-stuck',
    candidateAssign: (id: string) => `recruiting/candidates/${id}/assign-opening`,
    candidateDecision: (id: string) => `recruiting/candidates/${id}/decision`,
    candidateArchive: (id: string) => `recruiting/candidates/${id}/archive`,
    inboxSync: 'recruiting/candidates/inbox/sync',
    inboxSyncLatest: 'recruiting/candidates/inbox/sync/latest',
    mailbox: 'recruiting/mailbox',
    mailboxConnect: 'recruiting/mailbox/connect',
  },
  meetings: {
    collection: 'meetings',
    item: (id: string) => `meetings/${id}`,
    recording: (id: string) => `meetings/${id}/recording`,
    transcript: (id: string) => `meetings/${id}/transcript`,
    shares: (id: string) => `meetings/${id}/shares`,
    qa: (id: string) => `meetings/${id}/qa`,
    connections: 'me/calendar-connections',
    connect: (provider: string) => `me/calendar-connections/${provider}/connect`,
    disconnect: (provider: string) => `me/calendar-connections/${provider}`,
    providers: 'meeting/providers',
  },
} as const;

export function endpoint(path: string): string {
  return apiUrl(path);
}

export function isAuthSkipRefresh(url: string): boolean {
  return [API.auth.login, API.auth.refresh].some((path) =>
    url.includes(`/${path}`),
  );
}
